import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/513c4d98-c117-415c-aaa0-20587ff2b781/images/f8dd0a94b8e254c0a34852a57e9c96c84697dc92b50cf26a9d86c33295543d3d.png";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-center pt-28 pb-24 md:pb-32"
    >
      {/* Parallax molecular background */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#030704]/90 via-[#030704]/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#030704]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_75%_50%,rgba(16,185,129,0.18),transparent_55%)]" />
      </motion.div>

      {/* Subtle vertical scan-lines */}
      <div className="pointer-events-none absolute inset-0 z-[1] opacity-[0.06]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 80px)",
        }}
      />

      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full"
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-[#10B981]" />
          <span className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#10B981]">
            COLOSSUS BIO REFINERY · EST. INDIA
          </span>
        </motion.div>

        <motion.h1
          data-testid="hero-headline"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-[14vw] sm:text-[10vw] lg:text-[7vw] leading-[0.95] tracking-tighter font-light text-white max-w-[18ch]"
        >
          <span className="block">THE NEW</span>
          <span className="block">ARCHITECTURE</span>
          <span className="block italic font-extralight text-white/85">
            of life<span className="text-[#10B981]">.</span>
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-10 max-w-2xl font-body text-base md:text-lg text-white/70 leading-relaxed"
          data-testid="hero-subheadline"
        >
          Colossus is a high-precision fortress of bio-innovation. We transform
          renewable earth-resources into the world&apos;s most essential
          molecules through proprietary green-science.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-14 flex items-center gap-6"
        >
          <a
            href="#vault"
            data-testid="hero-cta-vault"
            className="group inline-flex items-center gap-3 rounded-full bg-[#10B981] px-6 py-3 text-sm font-medium text-[#03150E] hover:bg-[#10B981]/90 transition-all hover:gap-4"
          >
            Enter the Vault
            <ArrowDown className="h-4 w-4 -rotate-45 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
          <a
            href="#vision"
            data-testid="hero-cta-vision"
            className="text-xs uppercase tracking-[0.25em] text-white/60 hover:text-white border-b border-white/20 pb-1 transition-colors"
          >
            Discover the science
          </a>
        </motion.div>
      </motion.div>

      {/* Bottom HUD strip */}
      <div className="absolute bottom-0 inset-x-0 border-t border-white/[0.06] bg-[#030704]/40 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-[10px] uppercase tracking-[0.3em] text-white/50 font-body">
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] pulse-soft" />
            Closed-loop ecosystem
          </div>
          <div>100% Botanical Origin</div>
          <div className="hidden md:block">Pharma & Food grade</div>
          <div className="hidden md:block text-right">Made in India · Global</div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
